import { Search, SlidersHorizontal, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '../lib/cn'
import { Select } from './Select'

/**
 * Controls run a little larger below `sm` (40px tall) so they stay an easy
 * tap target. The 16px text is deliberate and not scaled down with the
 * height: iOS Safari auto-zooms the whole page whenever a focused input or
 * select renders below 16px. Both revert to the tighter desktop sizing at
 * `sm` and up.
 */
const CONTROL_BASE =
  'h-10 text-base sm:h-9 sm:text-sm rounded-lg border border-shell-border bg-shell outline-none transition-colors focus:border-brand-400 focus:ring-2 focus:ring-brand-100'

export function FilterBar({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-wrap items-center gap-2.5', className)}>
      <SlidersHorizontal size={14} className="hidden text-shell-text sm:block" />
      {children}
    </div>
  )
}

export function SearchInput({
  value,
  onChange,
  placeholder = 'Search...',
  className,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  className?: string
}) {
  return (
    <div className={cn('relative flex items-center', className)}>
      <Search size={15} className="absolute left-3 text-shell-text" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className={cn(CONTROL_BASE, 'w-full pl-9 pr-9 text-shell-text-active placeholder:text-shell-text')}
      />
      {value && (
        <button
          aria-label="Clear search"
          onClick={() => onChange('')}
          className="absolute right-2 flex h-8 w-8 items-center justify-center rounded-md text-shell-text transition-colors hover:bg-shell-border/50 hover:text-shell-text-active"
        >
          <X size={14} />
        </button>
      )}
    </div>
  )
}

export function FilterSelect({
  value,
  onChange,
  options,
  label,
}: {
  value: string
  onChange: (v: string) => void
  options: { label: string; value: string }[]
  label: string
}) {
  return (
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
      className={cn(CONTROL_BASE, 'min-w-[8rem] px-3 text-shell-text sm:min-w-0')}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </Select>
  )
}

export function SegmentedControl({
  value,
  onChange,
  options,
}: {
  value: string
  onChange: (v: string) => void
  options: { label: string; value: string }[]
}) {
  return (
    <div className="inline-flex items-center gap-0.5 rounded-lg bg-shell-border/50 p-1">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={cn(
            'relative rounded-md px-3 py-1.5 text-sm font-medium transition-colors sm:text-xs',
            value === opt.value
              ? 'bg-shell text-shell-text-active shadow-sm'
              : 'text-shell-text hover:text-shell-text-active',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}
